import { useState } from "react";
import { Loader2, Star, Clock, CheckCircle2, XCircle, AlertTriangle, CreditCard, Package, Shirt, RotateCcw, Ban, Eye, Shield, ShieldCheck } from "lucide-react";
import { useT } from "@/lib/i18n";
import { eur, fetchBlobUrl } from "@/lib/api";

export function DocButton({ fileId, label = "Dokument ansehen", testId }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  if (!fileId) return <span className="w-muted text-sm">–</span>;
  const view = async () => {
    setOpen(true);
    if (!url && !err) {
      try { setUrl(await fetchBlobUrl(fileId)); } catch { setErr("Datei konnte nicht geladen werden."); }
    }
  };
  return (
    <>
      <button type="button" className="w-btn w-btn-secondary !min-h-[38px] !px-3" onClick={view} data-testid={testId}><Eye size={15} aria-hidden="true" /> {label}</button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/70 grid place-items-center p-4" onClick={() => setOpen(false)} data-testid="doc-viewer">
          <div className="bg-white rounded-2xl p-3 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="font-semibold">{label}</span>
              <div className="flex gap-2">
                {url && <a href={url} target="_blank" rel="noreferrer" className="w-btn w-btn-secondary !min-h-[36px] !px-3">Öffnen</a>}
                <button className="w-btn w-btn-secondary !min-h-[36px] !px-3" onClick={() => setOpen(false)} data-testid="doc-viewer-close">Schließen</button>
              </div>
            </div>
            {err ? <p className="p-10 text-center text-[var(--w-error)]">{err}</p>
              : !url ? <p className="p-10 text-center w-muted">Lädt…</p>
                : <iframe src={url} title="Dokument" className="w-full h-[70vh] rounded-lg border border-line" />}
          </div>
        </div>
      )}
    </>
  );
}

const STATUS_STYLE = {
  requested: ["var(--w-info-soft)", "var(--w-info)", Clock],
  accepted: ["var(--w-warn-soft)", "var(--w-warn)", CreditCard],
  paid: ["var(--w-mint-soft)", "var(--w-mint-ink)", CreditCard],
  picked_up: ["var(--w-mint-soft)", "var(--w-mint-ink)", Package],
  washing: ["var(--w-mint-soft)", "var(--w-mint-ink)", Shirt],
  ready: ["var(--w-mint-soft)", "var(--w-mint-ink)", CheckCircle2],
  returned: ["var(--w-info-soft)", "var(--w-info)", Package],
  completed: ["var(--w-mint-soft)", "var(--w-mint-ink)", CheckCircle2],
  disputed: ["var(--w-error-soft)", "var(--w-error)", AlertTriangle],
  declined: ["#f3f4f6", "var(--w-ink-2)", XCircle],
  cancelled: ["#f3f4f6", "var(--w-ink-2)", Ban],
  expired: ["#f3f4f6", "var(--w-ink-2)", Clock],
  refunded: ["var(--w-warn-soft)", "var(--w-warn)", RotateCcw],
  auto_refunded: ["var(--w-warn-soft)", "var(--w-warn)", RotateCcw],
  hygiene_hold: ["var(--w-error-soft)", "var(--w-error)", AlertTriangle],
  returned_unprocessed: ["var(--w-info-soft)", "var(--w-info)", Package],
};

export function StatusBadge({ status, testId }) {
  const { t } = useT();
  const [bg, fg, Icon] = STATUS_STYLE[status] || ["#f3f4f6", "var(--w-ink-2)", Clock];
  return (
    <span className="w-chip" style={{ background: bg, color: fg }} data-testid={testId || `status-badge-${status}`}>
      <Icon size={16} aria-hidden="true" />
      {t(`status.${status}`)}
    </span>
  );
}

export function Chip({ children, tone = "neutral", testId }) {
  const tones = {
    neutral: ["#f3f4f6", "var(--w-ink)"], mint: ["var(--w-mint-soft)", "var(--w-mint-ink)"],
    warn: ["var(--w-warn-soft)", "var(--w-warn)"], error: ["var(--w-error-soft)", "var(--w-error)"], info: ["var(--w-info-soft)", "var(--w-info)"],
  };
  const [bg, fg] = tones[tone];
  return <span className="w-chip" style={{ background: bg, color: fg }} data-testid={testId}>{children}</span>;
}

const COV_LABEL = {
  paid_processing: "Entgeltliches Waschen/Trocknen", wash_dry: "Waschen & Trocknen", ironing: "Bügeln",
  processing_damage: "Bearbeitungsschäden", custody_damage: "Obhutsschäden", loss: "Verlust", transport: "Abholung/Transport",
};

export function InsuranceBadge({ insurance, testId = "insurance-badge", showDetails = true }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ins = insurance || { status: "none", tier: 0 };
  if (!ins.tier) {
    return <span className="w-chip" style={{ background: "#f3f4f6", color: "var(--w-ink-2)" }} data-testid={`${testId}-none`}><Shield size={16} aria-hidden="true" /> {t("ins.none")}</span>;
  }
  const premium = ins.tier === 2;
  const label = premium ? t("ins.badge2") : t("ins.badge1");
  const info = premium ? t("ins.badge2_info") : t("ins.badge1_info");
  const style = premium
    ? { background: "var(--w-mint-ink)", color: "#06281f" }
    : { background: "var(--w-mint-soft)", color: "var(--w-mint-ink)" };
  return (
    <div className="inline-block">
      <button type="button" className="w-chip" style={{ ...style, cursor: showDetails ? "pointer" : "default" }}
        onClick={() => showDetails && setOpen((o) => !o)} data-testid={testId} aria-expanded={open}>
        {premium ? <ShieldCheck size={16} aria-hidden="true" /> : <Shield size={16} aria-hidden="true" />} {label}
      </button>
      {open && showDetails && (
        <div className="mt-2 w-card p-3 text-sm max-w-md" data-testid={`${testId}-info`}>
          <p className="w-muted">{info}</p>
          {premium && ins.coverages && (
            <ul className="mt-2 space-y-1">
              {Object.entries(COV_LABEL).map(([k, l]) => (
                <li key={k} className="flex items-center gap-2">
                  {ins.coverages[k]
                    ? <CheckCircle2 size={14} className="text-mint-ink shrink-0" aria-hidden="true" />
                    : <XCircle size={14} className="text-ink-2 shrink-0 opacity-50" aria-hidden="true" />}
                  <span className={ins.coverages[k] ? "" : "opacity-60"}>{l}: {ins.coverages[k] ? "bestätigt" : "nicht bestätigt"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function Loader({ label }) {
  const { t } = useT();
  return (
    <div className="flex items-center gap-3 py-16 justify-center w-muted" role="status" data-testid="page-loader">
      <Loader2 className="animate-spin" aria-hidden="true" /> {label || t("common.loading")}
    </div>
  );
}

export function Stars({ value = 0, count }) {
  return (
    <span className="inline-flex items-center gap-1 font-semibold" aria-label={`${value} / 5`}>
      <Star size={16} fill="currentColor" style={{ color: "#b45309" }} aria-hidden="true" />
      {value ? Number(value).toFixed(1) : "Neu"}
      {count ? <span className="w-muted font-normal">({count})</span> : null}
    </span>
  );
}

export function PriceBreakdown({ quote, testId = "price-breakdown" }) {
  const { t } = useT();
  if (!quote) return null;
  return (
    <div data-testid={testId}>
      <ul className="space-y-2">
        {quote.lines.map((l) => (
          <li key={l.key} className="flex justify-between gap-4" data-testid={`price-line-${l.key}`}>
            <span>{l.key === "transport" && l.mode ? t(`transport.${l.mode}`) : t(`line.${l.key}`)} <span className="w-muted">{l.key === "base" || l.key === "dryer" || l.key === "folding" ? `${l.qty} kg × ${eur(l.unit)}` : l.key === "ironing" ? `${l.qty} × ${eur(l.unit)}` : l.key === "distance" ? `${String(l.qty).replace(".", ",")} km × ${eur(l.unit)}` : ""}</span></span>
            <span className="font-semibold tabular-nums">{eur(l.total)}</span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between items-baseline mt-4 pt-4 border-t border-line">
        <span className="font-bold text-lg">{t("price.total")}</span>
        <span className="font-extrabold text-2xl tabular-nums" data-testid={`${testId}-total`}>{eur(quote.total)}</span>
      </div>
      <p className="w-muted mt-1">{t("price.insurance")}</p>
    </div>
  );
}

export function Empty({ children, testId = "empty-state" }) {
  return <div className="w-card p-8 text-center w-muted" data-testid={testId}>{children}</div>;
}

export function Section({ title, children, action, testId }) {
  return (
    <section className="w-card p-5 md:p-6" data-testid={testId}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
